import type {
  PartnershipBlastBlocker,
  PartnershipBusinessCandidateDto,
  PartnershipContentRowDto,
  PartnershipBusinessCandidateSource,
  PartnershipBusinessIdSource,
  PartnershipBusinessOutcome,
  PartnershipCredentialKind,
  PartnershipDiscoveryCredentialDto,
  PartnershipDiscoveryCredentialKind,
  PartnershipDiscoveryOutcomeDto,
  PartnershipDiscoveryStatus,
  PartnershipEffectiveRightsStatus,
  PartnershipLaunchErrorKind,
  PartnershipLaunchPreviewDto,
  PartnershipLaunchTargetDto,
  PartnershipLaunchTargetResultDto,
  PartnershipLaunchValidationDto,
  PartnershipLaunchValidationKind,
  PartnershipLaunchWarning,
  PartnershipOrganicInsightsDto,
  PartnershipPlatform,
  PartnershipRightsStatus,
  PartnershipSyncIdentityDto,
  PartnershipWarning,
  PartnershipWindowAnchor,
} from "~/ui-rtk/api/types/partnerships.types"
import { formatNumber } from "~/ui-rtk/utils/format"

/**
 * Human copy for the partnership tracker enums + the blocker / warning reason
 * strings the API derives (contract §5). The API owns the rules; this file
 * only owns the words.
 */

export const RIGHTS_STATUS_LABEL: Record<PartnershipRightsStatus, string> = {
  META_PERMISSION_ONLY: "Meta permission only",
  AGREEMENT_PENDING: "Agreement pending",
  LICENSED: "Licensed",
  PERPETUAL: "Perpetual",
  REV_SHARE: "Rev share / affiliate",
}

export const EFFECTIVE_RIGHTS_STATUS_LABEL: Record<
  PartnershipEffectiveRightsStatus,
  string
> = {
  ...RIGHTS_STATUS_LABEL,
  EXPIRED: "Expired",
  REVOKED: "Revoked",
}

/** Options for the inline rights Select + the Add-creator dialog. */
export const RIGHTS_STATUS_OPTIONS: Array<{
  value: PartnershipRightsStatus
  label: string
}> = (Object.keys(RIGHTS_STATUS_LABEL) as PartnershipRightsStatus[]).map(
  value => ({ value, label: RIGHTS_STATUS_LABEL[value] }),
)

export const PLATFORM_LABEL: Record<PartnershipPlatform, string> = {
  INSTAGRAM: "Instagram",
  FACEBOOK: "Facebook",
  TIKTOK: "TikTok",
}

export const PLATFORM_OPTIONS: Array<{
  value: PartnershipPlatform
  label: string
}> = (Object.keys(PLATFORM_LABEL) as PartnershipPlatform[]).map(value => ({
  value,
  label: PLATFORM_LABEL[value],
}))

export const WINDOW_ANCHOR_LABEL: Record<PartnershipWindowAnchor, string> = {
  FIRST_SPEND: "From first spend",
  START_DATE: "From start date",
}

export const WINDOW_ANCHOR_OPTIONS: Array<{
  value: PartnershipWindowAnchor
  label: string
}> = (Object.keys(WINDOW_ANCHOR_LABEL) as PartnershipWindowAnchor[]).map(
  value => ({ value, label: WINDOW_ANCHOR_LABEL[value] }),
)

/**
 * The API's sentence for a launch whose `tags` lack a required category:
 * "Required tags missing: {labels}". It travels on the preview target
 * (`message`, or a warning string, or the dry-run message) and is the 400
 * message of a refused launch; `requiredTagLabelsOf` reads the labels back.
 */
export const REQUIRED_TAGS_MISSING_PREFIX = "Required tags missing"
const REQUIRED_TAGS_MISSING_RE = /^\s*Required tags missing:\s*(.+?)\s*$/i
/** What to do about it: the tags are picked in the launch dialog's Details step. */
export const REQUIRED_TAGS_MISSING_ACTION = "pick them in the launch dialog"

export const BLOCKER_COPY: Record<PartnershipBlastBlocker, string> = {
  platform_tiktok: "TikTok Spark Ads are not supported yet",
  platform_facebook: "Facebook partnership ads are not supported yet",
  manual_row_no_content_id:
    "No Meta post ID yet — a sync adds it from the post link (a row added with only an ad code needs adding again with its link)",
  meta_not_ad_ready: "Meta reports this post is not ad-ready",
  creator_revoked: "The creator revoked ads permission",
  video_needs_ad_code:
    "This Reel has never been boosted. Meta needs a partnership ad code from the creator (Reel → ⋯ → Partnership label & ads → Get partnership ad code) — paste it in the Ad code column — or boost the Reel once from Ads Manager.",
  // Label-less fallback; `describeBlocker` fills the labels in from the target.
  required_tags_missing: `${REQUIRED_TAGS_MISSING_PREFIX} — ${REQUIRED_TAGS_MISSING_ACTION}`,
}

/** The slots a preview target may carry the required-tag labels in. */
export type RequiredTagsCarrier = Partial<
  Pick<PartnershipLaunchTargetDto, "message" | "warnings" | "validation">
>

/**
 * The category labels behind a `required_tags_missing` blocker, e.g.
 * "Funnel, Gender": from the target's `message`, else a warning string the
 * API pushed, else the dry-run message. Null when the target carries none.
 */
export function requiredTagLabelsOf(
  target: RequiredTagsCarrier | null | undefined,
): string | null {
  if (!target) return null
  const candidates: Array<string | null | undefined> = [
    target.message,
    ...((target.warnings ?? []) as readonly string[]),
    target.validation?.message,
  ]
  for (const candidate of candidates) {
    const match =
      typeof candidate === "string"
        ? REQUIRED_TAGS_MISSING_RE.exec(candidate)
        : null
    if (match) return match[1]
  }
  return null
}

/**
 * `BLOCKER_COPY[blocker]`, with the labels filled in for
 * `required_tags_missing`: "Required tags missing: Funnel, Gender — pick
 * them in the launch dialog". Table rows never carry that blocker (it is
 * request-level), so callers without a target get the label-less line.
 */
export function describeBlocker(
  blocker: PartnershipBlastBlocker,
  target?: RequiredTagsCarrier | null,
): string {
  if (blocker === "required_tags_missing") {
    const labels = requiredTagLabelsOf(target)
    return labels
      ? `${REQUIRED_TAGS_MISSING_PREFIX}: ${labels} — ${REQUIRED_TAGS_MISSING_ACTION}`
      : BLOCKER_COPY.required_tags_missing
  }
  return BLOCKER_COPY[blocker]
}

/** Every blocker of a preview target in words, " · "-joined, labels included. Empty string when there is none. */
export function describeTargetBlockers(
  target: Pick<PartnershipLaunchTargetDto, "blockers"> & RequiredTagsCarrier,
): string {
  return target.blockers.map(b => describeBlocker(b, target)).join(" · ")
}

/**
 * Preview-level line from the body's `missingRequiredTags`: "Required tags
 * missing: Funnel, Gender — pick them in the launch dialog". Null when the
 * request covers every required category (or the API predates the field).
 */
export function describeMissingRequiredTags(
  preview: Pick<PartnershipLaunchPreviewDto, "missingRequiredTags">,
): string | null {
  const labels = preview.missingRequiredTags?.labels ?? []
  if (labels.length === 0) return null
  return `${REQUIRED_TAGS_MISSING_PREFIX}: ${labels.join(", ")} — ${REQUIRED_TAGS_MISSING_ACTION}`
}

export const WARNING_COPY: Record<PartnershipWarning, string> = {
  no_agreement: "No commercial agreement on file",
  window_ended: "Usage window has ended",
  window_unknown: "Licensed but no usage window set",
  live_ads_past_window: "Ads are live past the usage window",
  armed_under_paused_parent:
    "Ad is ACTIVE under a paused ad set/campaign — reactivating the parent restarts it",
  not_seen_in_latest_sync: "Meta did not return this post in the latest sync",
}

/** A launch target's warnings: the row's, plus the one the target adds (mirrors the API's PARTNERSHIP_TARGET_WARNINGS). */
export const LAUNCH_WARNING_COPY: Record<PartnershipLaunchWarning, string> = {
  ...WARNING_COPY,
  already_in_adset: "This post already has an ad in this ad set",
}

/** Meta's `ad_usage` values → chip label. Unknown values fall back to the raw string. */
export const AD_USAGE_LABEL: Record<string, string> = {
  never_used: "Never used",
  previously_used: "Used before",
  active: "Active",
}

/** Meta's `ad_eligibility` values → chip label. */
export const AD_ELIGIBILITY_LABEL: Record<string, string> = {
  ad_ready: "Ad ready",
  needs_attention: "Needs attention",
  ineligible: "Ineligible",
  excluded: "Excluded",
}

/** Meta's `permission_type` values → tooltip label. */
export const PERMISSION_TYPE_LABEL: Record<string, string> = {
  account_level_ad_partnership: "Account-level ad partnership",
  allow_to_boost_toggle: "Creator allowed boosting on this post",
  ad_code: "Creator ad code",
}

/** Turn `needs_attention` / `SOME_STATUS` into "Needs attention". */
export function humanize(value: string): string {
  const words = value.replace(/[_-]+/g, " ").trim().toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/* ----- Business Manager id (design §1.1) ----- */

export const BUSINESS_ID_LABEL =
  "Business Manager used for Meta content discovery"

/** Always visible under the label. */
export const BUSINESS_ID_HELP =
  "The Meta business portfolio the connected user reaches this brand's Instagram account through. For brands Marathon runs as an agency this is Marathon Data (1191201865171582); for brands connected by their own team it is the brand's own portfolio. It is the business_id in your Creator Marketing Hub URL."

/** Tooltip on the info icon. */
export const BUSINESS_ID_WHERE_TO_FIND =
  "Where to find it: Meta Business Suite → Business settings → Business info → Business ID. The creator's business id does NOT go here."

export const BUSINESS_ID_SOURCE_LABEL: Record<
  PartnershipBusinessIdSource,
  string
> = {
  company: "company override",
  default: "Marathon default",
  account: "ad account's business",
  none: "not set",
}

export const BUSINESS_CANDIDATE_SOURCE_LABEL: Record<
  PartnershipBusinessCandidateSource,
  string
> = {
  company: "company override",
  default: "Marathon default",
  account: "ad account's business",
  membership: "connected user is a member",
  page: "brand Page's business",
}

/** `{name} · {id}` when the name is known, else just the id. */
export function formatBusinessLabel(
  id: string | null,
  name: string | null,
): string {
  if (!id) return "none"
  return name ? `${name} · ${id}` : id
}

export type BusinessOutcomeTone = "green" | "amber" | "red" | "grey"

const BUSINESS_OUTCOME_CHIP: Record<
  PartnershipBusinessOutcome,
  { label: string; tone: BusinessOutcomeTone }
> = {
  ok: { label: "Works", tone: "green" },
  not_member: { label: "Not a member", tone: "red" },
  brand_not_in_business: {
    label: "Brand not shared with this business",
    tone: "amber",
  },
  scope_missing: { label: "Missing branded-content permission", tone: "amber" },
  business_permission_missing: {
    label: "Needs business-level permission",
    tone: "amber",
  },
  unknown_error: { label: "Error", tone: "red" },
  skipped: { label: "Skipped", tone: "grey" },
}

/** The API probes one full page of the discovery edge; a count at the page size means "at least this many". */
export const BUSINESS_PROBE_PAGE_SIZE = 50

/** Chip for a probed candidate: `Works · N posts` (`50+ posts` at the page cap) / `Not a member` / … */
export function businessOutcomeChip(
  candidate: Pick<PartnershipBusinessCandidateDto, "outcome" | "sampleCount">,
): { label: string; tone: BusinessOutcomeTone } {
  const chip = BUSINESS_OUTCOME_CHIP[candidate.outcome]
  if (candidate.outcome === "ok" && candidate.sampleCount !== null) {
    const n = candidate.sampleCount
    const count =
      n >= BUSINESS_PROBE_PAGE_SIZE ? `${BUSINESS_PROBE_PAGE_SIZE}+` : n
    return {
      ...chip,
      label: `${chip.label} · ${count} post${n === 1 ? "" : "s"}`,
    }
  }
  return chip
}

/**
 * Which credentials a probed candidate was seen through, so the fix names
 * the right thing to change: the brand connector's credential (what the
 * brand's own reads/writes run on) and the discovery credential (what the
 * probe itself ran on — never a system user, Meta refuses them there).
 */
export interface BusinessFixCredentialContext {
  brand: PartnershipCredentialKind
  discovery: PartnershipDiscoveryCredentialKind
  discoveryName: string | null
}

/**
 * What to do about a candidate that does not work. `null` for `ok`.
 * `userName` is the connected Meta user (falls back to "The connected user").
 * Without `credential` the copy assumes a person's login all the way through
 * (the pre-system-user world).
 */
export function businessOutcomeFix(
  candidate: Pick<PartnershipBusinessCandidateDto, "outcome" | "detail">,
  userName: string | null,
  credential?: BusinessFixCredentialContext,
): string | null {
  const user = userName ?? "The connected Meta user"
  const systemUserBrand = credential?.brand === "meta_system_user"
  const agencyLogin = credential?.discovery === "agency"
  const agency = credential?.discoveryName
    ? `The agency login ${credential.discoveryName}`
    : "The agency login"
  // A company's Meta access account user (APP-566): fixed in Settings, not Partnerships.
  const accountUserLogin = credential?.discovery === "account_user"
  const accountUser = credential?.discoveryName
    ? `The account user ${credential.discoveryName}`
    : "The account user"
  switch (candidate.outcome) {
    case "ok":
      return null
    case "not_member":
      if (accountUserLogin) {
        return `${accountUser} isn't a member of this business. Add them in Business settings → Users → People, then reconnect it in Settings → Company → Meta access.`
      }
      if (agencyLogin) {
        return `${agency} isn't a member of this business. Add them in Business settings → Users → People, then set the agency discovery credential again.`
      }
      return `${user} isn't a member of this business. Add them in Business settings → Users → People, then reconnect the Instagram Insights connector.`
    case "brand_not_in_business":
      if (systemUserBrand) {
        return "This brand's Instagram account isn't owned by or shared with this business. Share it with the business, assign it — with the brand Page and ad account — to the system user (Business settings → Users → System users → Assign assets), then Detect again."
      }
      return "This brand's Instagram account isn't owned by or shared with this business."
    case "scope_missing":
      if (accountUserLogin) {
        return `${accountUser} is missing the instagram_branded_content_ads_brand permission. Tick it in Facebook's dialog, then reconnect it in Settings → Company → Meta access.`
      }
      if (agencyLogin) {
        return `${agency} is missing the instagram_branded_content_ads_brand permission. Set the agency discovery credential again with a Marathon app-role login that has it.`
      }
      if (systemUserBrand) {
        return "Generate a new token for the system user with instagram_branded_content_ads_brand ticked and set it on Setup → Connectors → Meta Advanced (super admin), then Detect again."
      }
      return "The connected login is missing the instagram_branded_content_ads_brand permission. Reconnect the Instagram Insights connector with a Marathon app-role user."
    case "business_permission_missing":
      if (systemUserBrand) {
        return "Meta refuses system users for content discovery; add or repair the agency discovery credential (Manage, above), then Detect again."
      }
      return "The credential can act on this brand's assets but cannot view this business's assets, which content discovery requires. For a system user, grant it that permission (or admin access) in Business settings → Users → System users; for a person, ask a business admin to update their permissions."
    case "skipped":
      return (
        candidate.detail ??
        "Pick the brand Instagram account on the Instagram Insights connector first."
      )
    case "unknown_error":
      return candidate.detail
        ? `Meta returned an error: ${candidate.detail}`
        : "Meta returned an unexpected error. Try again in a minute."
  }
}

/* ----- Sync health (design §1.3) ----- */

/** DOM id of the Business Manager panel; the sync-health panel links to it. */
export const BUSINESS_MANAGER_PANEL_ID = "business-manager-panel"

/* ----- Credentials (MD-5501 hybrid: brand credential vs discovery credential) ----- */

export const CREDENTIAL_KIND_LABEL: Record<PartnershipCredentialKind, string> =
  {
    person: "personal login",
    meta_system_user: "system user",
  }

/** "Brand credential: system user marathon-duer" / "Brand credential: personal login Tom". */
export function formatBrandCredentialLine(
  kind: PartnershipCredentialKind,
  name: string | null,
): string {
  const label = CREDENTIAL_KIND_LABEL[kind]
  return `Brand credential: ${name ? `${label} ${name}` : label}`
}

export type CredentialLineTone = "ok" | "warning" | "neutral"

/**
 * "Discovery credential: agency login Tom · covers this brand" (ok),
 * "… · not working — {the failure discovery recorded}" (warning),
 * "… · does not cover this brand — reconnect it including @brand" (warning),
 * "Discovery credential: connector login" (neutral), or
 * "Discovery credential: none — add one" (warning).
 */
export function formatDiscoveryCredentialLine(
  discovery: PartnershipDiscoveryCredentialDto,
  brandIgUsername: string | null,
): { text: string; tone: CredentialLineTone } {
  switch (discovery.kind) {
    case "connector_person":
      return {
        text: discovery.name
          ? `Discovery credential: connector login ${discovery.name}`
          : "Discovery credential: connector login",
        tone: "neutral",
      }
    case "agency":
    case "account_user": {
      const label =
        discovery.kind === "account_user" ? "account user" : "agency login"
      const who = discovery.name ? `${label} ${discovery.name}` : label
      // Its grant can still name the brand while discovery cannot use it.
      if (discovery.problem) {
        return {
          text: `Discovery credential: ${who} · not working — ${discovery.problem}`,
          tone: "warning",
        }
      }
      if (discovery.coversBrand === true) {
        return {
          text: `Discovery credential: ${who} · covers this brand`,
          tone: "ok",
        }
      }
      if (discovery.coversBrand === false) {
        const brand = brandIgUsername
          ? `@${brandIgUsername}`
          : "this brand's Instagram account"
        return {
          text: `Discovery credential: ${who} · does not cover this brand — reconnect it including ${brand}`,
          tone: "warning",
        }
      }
      return { text: `Discovery credential: ${who}`, tone: "neutral" }
    }
    case "none":
      return {
        text: "Discovery credential: none — add one",
        tone: "warning",
      }
  }
}

/**
 * Sync-health suffix naming what discovery ran on: "(via agency login Tom)"
 * / "(via account user Tom)" / "(via connector login)". Null when unknown —
 * identities stored by runs before the hybrid shipped carry no kind.
 */
export function discoveryCredentialSuffix(
  identity: Pick<
    PartnershipSyncIdentityDto,
    "discoveryCredentialKind" | "discoveryCredentialName"
  > | null,
): string | null {
  if (!identity) return null
  switch (identity.discoveryCredentialKind) {
    case "agency":
      return identity.discoveryCredentialName
        ? `(via agency login ${identity.discoveryCredentialName})`
        : "(via agency login)"
    case "account_user":
      return identity.discoveryCredentialName
        ? `(via account user ${identity.discoveryCredentialName})`
        : "(via account user)"
    case "connector_person":
      return "(via connector login)"
    default:
      return null
  }
}

/** The sync's skip reason when a system-user brand has no agency discovery credential. */
export function isNoAgencyCredentialSkip(detail: string | null): boolean {
  return /no agency discovery credential/i.test(detail ?? "")
}

const NO_AGENCY_CREDENTIAL_EXPLANATION =
  "This brand's connector holds a system user, which Meta refuses for content discovery, and no agency discovery credential is set. A Marathon admin adds one in the Business Manager panel above (Manage), then Refresh from Meta."

/* ----- Agency discovery credential dialog ----- */

export const AGENCY_CREDENTIAL_TITLE = "Agency discovery credential"

export const AGENCY_CREDENTIAL_HELP =
  "Content discovery is the one Meta call that refuses system users for partner-shared content, so brands connected with a system user discover through this single Marathon login instead. It is stored encrypted, used only for the discovery edge, and never shown again. The Business Manager panel says whether it covers this brand."

/**
 * Above "Connect with Facebook". Meta stores one authorisation per person
 * and app, shared by every credential that person holds with us — so the
 * asset selection made in this login is not confined to the agency
 * credential.
 */
export const AGENCY_CREDENTIAL_OAUTH_HELP =
  "Use a Marathon login whose Meta authorisation covers every brand you discover for. What you tick in Meta's dialog becomes that login's authorisation for Marathon everywhere."

export const AGENCY_CREDENTIAL_CONNECT_LABEL = "Connect with Facebook"

/** Disclosure that reveals the paste-a-token fallback. */
export const AGENCY_CREDENTIAL_PASTE_TOGGLE = "Or paste a token"

export const AGENCY_CREDENTIAL_INPUT_LABEL = "Agency login access token"

/**
 * The paste path only refreshes the current discovery-default person (APP-566
 * T2); another Facebook account is added in Meta access, by OAuth only.
 */
export const AGENCY_CREDENTIAL_PASTE_PLACEHOLDER =
  "Paste a new token for the same Facebook account (another person: use Meta access)"

export const AGENCY_CREDENTIAL_META_ACCESS_LINK =
  "Manage all account users in Settings → Company → Meta access"

export const AGENCY_CREDENTIAL_REMOVE_CONFIRM =
  "Remove the agency login? Content discovery stops for every brand connected with a system user until a new one is set."

/* ----- Sync health (design §1.3) ----- */

/** Where a super admin sets a system user's token (as in the API). */
const SYSTEM_USER_TOKEN_LOCATION =
  "Setup → Connectors → Meta Advanced (super admin)"

/**
 * Known sync-warning fingerprints → plain-language fix text. Order matters:
 * the first matching pattern wins. Anything unmatched is shown verbatim.
 *
 * The fix-it sentences mirror the API's `classifyDiscoveryError` verbatim
 * (apps/api/src/services/partnerships/meta-partnership.service.ts), so a
 * warning the API already classified reads the same here and the user never
 * sees two remedies for one problem — change both together.
 *
 * `systemUserText` is the same fix for a brand connector holding a system
 * user, mirroring the API's `adaptBrandCredentialFixText`: the token is the
 * Meta Advanced credential's, so a new one is set there.
 */
const SYNC_WARNING_EXPLANATIONS: Array<{
  pattern: RegExp
  text: string
  systemUserText?: string
}> = [
  {
    pattern:
      /\bcode 190\b|oauthexception|error validating access token|session has expired|has expired or was revoked/i,
    text: "The Meta login behind the Instagram Insights connector has expired or was revoked. Reconnect the Instagram Insights connector, then Refresh from Meta.",
    systemUserText: `The system user token has expired or was revoked. Generate a new token for the system user and set it on ${SYSTEM_USER_TOKEN_LOCATION}, then Refresh from Meta.`,
  },
  {
    pattern: /granular permission|\bcode 3\b|\(#3\)/i,
    text: "The connected Meta user can no longer act on this brand's Instagram account (Meta error code 3). Restore their access to the account in Business settings → Accounts → Instagram accounts, then reconnect the Instagram Insights connector.",
    systemUserText:
      "The system user can no longer act on this brand's Instagram account (Meta error code 3). Assign the account to the system user again in Business settings → Users → System users → Assign assets (Content, Ads and Insights), then Refresh from Meta.",
  },
  {
    pattern: /restricted to certain users|isn't a member of this business/i,
    text: "The connected Meta user isn't a member of this business. Add them in Business settings → Users → People, then reconnect the Instagram Insights connector.",
    systemUserText:
      "The system user isn't a member of this business. Check that the brand's assets are assigned to it in Business settings → Users → System users → Assign assets, then Refresh from Meta.",
  },
  {
    pattern:
      /does not belong to the specified business|isn't owned by or shared with this business/i,
    text: "This brand's Instagram account isn't owned by or shared with this business. Share it with that business in Business settings → Accounts → Instagram accounts, or pick the business that owns it.",
  },
  {
    pattern: /insufficient permission scope|branded_content_ads_brand/i,
    text: "The connected credential lacks a branded-content permission (instagram_branded_content_ads_brand / facebook_branded_content_ads_brand). Reconnect the Instagram Insights connector with a Marathon app-role user.",
    systemUserText: `The system user lacks a branded-content permission (instagram_branded_content_ads_brand / facebook_branded_content_ads_brand). Generate a new token for the system user with that permission ticked and set it on ${SYSTEM_USER_TOKEN_LOCATION}.`,
  },
  {
    pattern: /no agency discovery credential/i,
    text: NO_AGENCY_CREDENTIAL_EXPLANATION,
  },
  {
    pattern: /no business manager id/i,
    text: "Content discovery needs a Business Manager id. Use Detect or enter one in the Business Manager panel above, then Refresh from Meta.",
  },
  {
    pattern: /no brand instagram account (id|is selected)/i,
    text: "Pick the brand Instagram account on the Instagram Insights connector — discovery has nothing to look up without it.",
  },
  {
    pattern: /no brand page could be resolved/i,
    text: "No Facebook Page is linked to the ad account, so Facebook discovery was skipped. Instagram discovery is unaffected.",
  },
  {
    pattern: /page cap|request budget|truncated|partial/i,
    text: "Meta returned more posts than one sync reads. The table shows what was fetched; the rest arrive on later syncs.",
  },
]

/** The `(Meta: …)` tail the API appends to a classified warning. */
const META_NOTE_RE = /\s*\(Meta: (.+)\)\s*$/

/**
 * Splits a classified warning into the fix-it text and Meta's own words (the
 * trailing `(Meta: …)` segment), when the API attached one.
 */
export function splitMetaNote(raw: string): {
  text: string
  metaNote: string | null
} {
  const match = META_NOTE_RE.exec(raw)
  if (!match) return { text: raw, metaNote: null }
  return { text: raw.slice(0, match.index), metaNote: match[1] }
}

/**
 * A discovery error the API already rewrote for the agency discovery login
 * (`adaptDiscoveryFixText`): its remedy names that credential, and the
 * connector fingerprints above would turn it back into "reconnect the
 * Instagram Insights connector" — which on a system-user brand swaps the
 * system user for a personal login. The no-credential case keeps its own entry.
 */
const AGENCY_REMEDY_RE = /\bagency discovery (login|credential)\b/i
const NO_AGENCY_CREDENTIAL_RE = /no agency discovery credential/i

/**
 * A creator-permissions read on the brand credential. On a system-user
 * connector the API already wrote the system-user remedy
 * (`adaptBrandCredentialFixText`) or passed Meta's own refusal through; the
 * connector fingerprints would turn either into "reconnect the Instagram
 * Insights connector", which swaps the system user for a personal login.
 */
const BRAND_CREDENTIAL_WARNING_RE = /^Creator permissions could not be read/i

/**
 * The API's system-user remedy names the system user. The creator-permissions
 * phase can run on one while the run reports the person's login (the edit
 * switch, or the tier order with the branded-content scope, APP-566 T3), so
 * the warning's own text decides, not only the run's credential.
 */
const SYSTEM_USER_REMEDY_RE = /\bsystem user/i

/**
 * The API's account-user remedy (APP-566 T5): the creator-permissions phase
 * ran on the company's account user, so the fix is in Meta access, not on the
 * connector the run reports. Checked only for creator-permissions warnings,
 * so discovery warnings explain as before.
 */
const META_ACCESS_REMEDY_RE = /\bMeta access login\b/i

/**
 * Plain-language explanation for a sync warning. Returns the raw text (minus
 * any `(Meta: …)` tail) when no fingerprint matches, so callers can show it
 * as-is. `credentialKind` is the brand connector's credential the run used
 * (or holds today, for a failed run that stored none); a system user gets the
 * system-user remedy.
 */
export function explainSyncWarning(
  raw: string,
  credentialKind?: PartnershipCredentialKind | null,
): string {
  if (AGENCY_REMEDY_RE.test(raw) && !NO_AGENCY_CREDENTIAL_RE.test(raw)) {
    return splitMetaNote(raw).text
  }
  if (
    BRAND_CREDENTIAL_WARNING_RE.test(raw) &&
    (credentialKind === "meta_system_user" ||
      SYSTEM_USER_REMEDY_RE.test(splitMetaNote(raw).text) ||
      META_ACCESS_REMEDY_RE.test(splitMetaNote(raw).text))
  ) {
    return splitMetaNote(raw).text
  }
  for (const { pattern, text, systemUserText } of SYNC_WARNING_EXPLANATIONS) {
    if (pattern.test(raw)) {
      return credentialKind === "meta_system_user"
        ? (systemUserText ?? text)
        : text
    }
  }
  return splitMetaNote(raw).text
}

/**
 * Warnings older API builds pushed for a Facebook skip (the scope is never
 * granted to client-connected users, so every brand carried one, forever).
 * Newer builds keep the reason on the Facebook status line only; the panel
 * drops these from `warnings` so an old stored result cannot turn it amber.
 */
export function isInformationalSyncWarning(raw: string): boolean {
  return /^Facebook content discovery skipped:/i.test(raw)
}

/**
 * Meta-side flakiness the user cannot act on: a query that still answered 5xx
 * after retries, a Meta rate limit that stopped the target early (it clears on
 * its own; the next sync runs it again), or a per-creator query Meta refused.
 * Listed (coverage may be incomplete) but never the reason the panel turns amber.
 */
export function isMetaTransientSyncWarning(raw: string): boolean {
  return /failed on Meta's side \(HTTP 5\d\d\)|Meta is rate-limiting the discovery login|Meta refused the per-creator queries/i.test(
    raw,
  )
}

export const DISCOVERY_STATUS_LABEL: Record<
  PartnershipDiscoveryStatus,
  string
> = {
  ok: "OK",
  partial: "partial",
  unavailable: "unavailable",
  skipped: "skipped",
}

/** Facebook discovery needs a scope Meta grants only to app-role users; a skip is the normal state for a client-connected brand. */
export const FACEBOOK_SKIPPED_LINE =
  "Facebook discovery: skipped — not enabled for this connection (Instagram discovery is unaffected)"

/**
 * One line per platform for the sync-health panel:
 *   "Instagram discovery: OK · 12 posts from 3 queries"
 *   "Instagram discovery: partial · 40 posts from 8 queries — {the service's own detail, verbatim}"
 *   "Instagram discovery: unavailable — {plain reason}"
 *   "Facebook discovery: skipped — not enabled for this connection (…)"
 *
 * A `partial` detail is several joined sentences (500s, budget, page cap);
 * running it through the fingerprint map would collapse them into whichever
 * one matched, so it is shown as the service wrote it.
 */
export function formatDiscoveryLine(
  platform: "Instagram" | "Facebook",
  outcome: PartnershipDiscoveryOutcomeDto,
): string {
  const head = `${platform} discovery: ${DISCOVERY_STATUS_LABEL[outcome.status]}`
  const counts = `${outcome.items} post${outcome.items === 1 ? "" : "s"} from ${outcome.queries} quer${outcome.queries === 1 ? "y" : "ies"}`
  switch (outcome.status) {
    case "ok":
      return `${head} · ${counts}`
    case "partial":
      return outcome.detail
        ? `${head} · ${counts} — ${outcome.detail}`
        : `${head} · ${counts}`
    case "skipped":
      if (platform === "Facebook") return FACEBOOK_SKIPPED_LINE
      return outcome.detail
        ? `${head} — ${explainSyncWarning(outcome.detail)}`
        : head
    case "unavailable":
      return outcome.detail
        ? `${head} — ${explainSyncWarning(outcome.detail)}`
        : head
  }
}

/* ----- Creator-side steps for the empty state ----- */

export const CREATOR_APPROVAL_STEPS =
  "Creators approve the brand in Instagram → Professional dashboard → Partnership ads → Approve; their eligible posts show up here after the next Refresh from Meta."

/* ----- Engagement ----- */

/**
 * Compact engagement line for the Content cell: "53.9K views · 2.1K likes ·
 * 44 comments". Null when none of the headline counts is present.
 */
export function formatEngagement(
  insights: PartnershipOrganicInsightsDto | null,
): { compact: string; full: string } | null {
  if (!insights) return null
  const parts: Array<[number, string]> = []
  const push = (value: number | null, noun: string) => {
    if (typeof value === "number") parts.push([value, noun])
  }
  push(insights.views, "views")
  push(insights.likes, "likes")
  push(insights.comments, "comments")
  push(insights.shares, "shares")
  push(insights.saves, "saves")
  if (parts.length === 0) return null
  const compact = parts
    .slice(0, 3)
    .map(
      ([n, noun]) =>
        `${formatNumber(n, { compact: true, decimals: 1 })} ${noun}`,
    )
    .join(" · ")
  const full = parts
    .map(([n, noun]) => `${formatNumber(n)} ${noun}`)
    .join(" · ")
  return { compact, full }
}

/* ----- Launch as paused partnership ads (MD-5501) ----- */

/**
 * Every ad the launch creates is PAUSED and stays that way until someone
 * activates it in Ads Manager — the words below never promise otherwise.
 */

/** Row button in the Launch column. */
export const LAUNCH_BUTTON_LABEL = "Launch as ad"

/** Bulk-bar button: "Launch as ad" for one row, "Launch {n} as ads" beyond. */
export function launchButtonLabel(count: number): string {
  return count === 1 ? LAUNCH_BUTTON_LABEL : `Launch ${count} as ads`
}

/**
 * The confirm button. No count: the job tries every target the pre-flight
 * passes, including ones Meta refused in the dry run, so the preview's
 * "Ready" count is not a promise.
 */
export const CREATE_PAUSED_ADS_LABEL = "Create paused ads"

/** Preview summary, after "{n} of {m} targets pass Meta's dry run." when some but not all did. */
export const LAUNCH_PREVIEW_REST_NOTE =
  "Confirm also tries the ones Meta refused in the preview — each ad's result is reported."

/** Creating step: the target count the API accepted (`POST /launch` → `targets`). */
export function creatingPausedAdsLabel(targets: number): string {
  return `Creating up to ${targets} paused ad${targets === 1 ? "" : "s"}`
}

/** Launch-column chip when the row's only blocker is `video_needs_ad_code`. */
export const NEEDS_AD_CODE_CHIP = "Needs creator code"

/**
 * Launch-column tooltip and bulk-bar reason when the row is launchable but
 * the company is not (`settings.blastAvailable === false`).
 */
export const LAUNCH_UNAVAILABLE_TOOLTIP =
  "Launching needs a Meta ad account bound with write access and a synced brand identity (Instagram account + Page). Reconnect the Instagram Insights connector or Refresh from Meta, then try again."

/** The "Ready to launch" filter checkbox (rows with no launch blockers). */
export const READY_TO_LAUNCH_LABEL = "Ready to launch"
export const READY_TO_LAUNCH_TOOLTIP = "Posts with no launch blockers"

/**
 * Every blocker but `video_needs_ad_code`, the one Meta gets to overrule (the
 * row's `adUsage` is a nightly snapshot) — the preview's dry run decides it.
 * Mirrors the API's `PartnershipLaunchService.hardBlockers`.
 */
export function hardLaunchBlockers(
  blockers: PartnershipBlastBlocker[],
): PartnershipBlastBlocker[] {
  return blockers.filter(b => b !== "video_needs_ad_code")
}

/** The row can go to the launch dialog: no hard blockers. */
function canTryLaunch(row: PartnershipContentRowDto): boolean {
  return hardLaunchBlockers(row.derived.blockers).length === 0
}

/**
 * Posts per launch. Mirrors the API's `PartnershipLaunchDto.contentIds`
 * `@ArrayMaxSize(20)` (apps/api/src/dto/partnerships/partnership-launch.dto.ts)
 * — change both.
 */
const MAX_LAUNCH_POSTS = 20

/**
 * Bulk-bar tooltip when the selection cannot launch as a whole:
 * "2 of the selected posts can't be launched: reason; reason" — or the
 * company-level reason when launching is unavailable altogether, or the
 * API's post cap. Null when every selected row can launch.
 */
export function describeUnlaunchable(
  rows: PartnershipContentRowDto[],
  launchAvailable: boolean,
): string | null {
  if (!launchAvailable) return LAUNCH_UNAVAILABLE_TOOLTIP
  if (rows.length > MAX_LAUNCH_POSTS) {
    return `Launch at most ${MAX_LAUNCH_POSTS} posts at a time — ${rows.length} selected`
  }
  const blocked = rows.filter(r => !canTryLaunch(r))
  if (blocked.length === 0) return null
  const reasons = [...new Set(blocked.flatMap(r => r.derived.blockers))].map(
    b => describeBlocker(b),
  )
  return `${blocked.length} of the selected posts can't be launched: ${reasons.join("; ")}`
}

/**
 * Mirror of the API's `composePartnershipAdName` / `partnershipExpiryLabel` /
 * `compactCaption` (apps/api/src/services/partnerships/partnership-launch.service.ts
 * — change both):
 * "{handle} · {rights} · {to YYYY-MM-DD | {N}d from first spend | no expiry} · {caption ≤ 40}",
 * at most 100 characters; with a filled naming convention (the tag
 * composer's name) "{convention} · {handle} · {rights} · {expiry}". Fills the
 * single-row name field; bulk launches, and a single row at a company with a
 * convention unless its name is typed, are named server-side, so the preview
 * step is the source of truth.
 */
export const AD_NAME_DEFAULT_MAX = 100
const AD_NAME_CAPTION_MAX = 40
const AD_NAME_SEPARATOR = " · "
/** The launch request's `adName` ceiling (API DTO `@MaxLength(255)`). */
export const AD_NAME_MAX = 255

/** The post caption on one line, at most 40 characters (an ellipsis marks a cut); null when blank. */
export function compactCaption(
  caption: string | null | undefined,
): string | null {
  const oneLine = (caption ?? "").replace(/\s+/g, " ").trim()
  if (!oneLine) return null
  return oneLine.length <= AD_NAME_CAPTION_MAX
    ? oneLine
    : `${oneLine.slice(0, AD_NAME_CAPTION_MAX - 1).trimEnd()}…`
}

/**
 * The window end; "{N}d from first spend" while a first-spend window with a
 * length has not started (no end date yet); "no expiry" only when nothing ends it.
 */
function partnershipExpiryLabel(
  derived: PartnershipContentRowDto["derived"],
): string {
  if (derived.effectiveEnd) return `to ${derived.effectiveEnd}`
  if (
    derived.windowAnchor === "FIRST_SPEND" &&
    derived.usageDays != null &&
    !derived.windowStart
  ) {
    return `${derived.usageDays}d from first spend`
  }
  return "no expiry"
}

export function composePartnershipAdName(
  row: PartnershipContentRowDto,
  conventionName?: string,
): string {
  const handle =
    (row.partnership?.creatorHandle ?? row.authorDisplayName)?.trim() ||
    "creator"
  const parts = [
    handle,
    EFFECTIVE_RIGHTS_STATUS_LABEL[row.derived.effectiveRightsStatus],
    partnershipExpiryLabel(row.derived),
  ]
  if (conventionName) return [conventionName, ...parts].join(AD_NAME_SEPARATOR)
  const caption = compactCaption(row.caption)
  if (caption) parts.push(caption)
  return parts.join(AD_NAME_SEPARATOR).slice(0, AD_NAME_DEFAULT_MAX).trim()
}

/** Dialog title: "Launch @alice's post as a paused ad" / "Launch 3 posts as paused ads". */
export function launchDialogTitle(rows: PartnershipContentRowDto[]): string {
  if (rows.length === 1) {
    const handle = rows[0].partnership?.creatorHandle
    return handle
      ? `Launch @${handle}'s post as a paused ad`
      : "Launch this post as a paused ad"
  }
  return `Launch ${rows.length} posts as paused ads`
}

export const LAUNCH_STEP_COPY = {
  adsets:
    "Pick the Meta ad sets to create one paused ad per post in. Nothing is activated.",
  details: "Name, landing page and call to action for the new ads.",
  preview:
    "Meta dry-runs every target without creating anything. Ads are created PAUSED; you activate them in Ads Manager.",
  creating: "Creating paused ads through Meta — this takes a moment per ad.",
  done: "Every ad below was created PAUSED. Activate them from Ads Manager when ready.",
} as const

/** Bulk launches are named server-side; the field is read-only and says so. */
export const BULK_AD_NAME_NOTE =
  "Named automatically per post: creator handle · rights · usage window · caption."
/** The same when the company has an ad naming convention (AdForge `nameTemplate`): the server fills it from the launch's tags. */
export const BULK_AD_NAME_CONVENTION_NOTE =
  "Named automatically per post: your ad naming convention, filled from the tags you pick, then creator handle · rights · usage window."

/** The single-row name field's placeholder when the company has a naming convention and no name is typed. */
export const AD_NAME_CONVENTION_PLACEHOLDER =
  "Named by your ad naming convention (shown in the preview) — or type a name"
export const AD_NAME_REQUIRED = "Give the ad a name."
export const LANDING_PAGE_INVALID = "Landing page must be an https:// URL."
/** The one CTA the launch creates with. */
export const CTA_LABEL = "Shop now"

export const PREVIEW_FAILED = "Couldn't preview the launch. Please try again."
export const LAUNCH_FAILED = "Couldn't start the launch. Please try again."
export const LAUNCH_IN_BACKGROUND_NOTE =
  "Still creating in the background — the Ads column updates as they land."

/** Preview step: what the creator has to do for a never-boosted Reel. */
export const AD_CODE_CREATOR_STEPS =
  "Ask the creator for a partnership ad code (Reel → ⋯ → Partnership label & ads → Get partnership ad code), paste it in the Ad code column, then preview again — or boost the Reel once from Ads Manager."

/** Meta's validate-only dry run, per outcome class. */
export const LAUNCH_VALIDATION_COPY: Record<
  PartnershipLaunchValidationKind,
  string
> = {
  video_needs_first_boost_or_code:
    "Meta can't use this Reel yet: it has never been boosted and no creator ad code is set.",
  link_required: "This ad set needs a website link — set a landing page.",
  content_rejected:
    "Meta says this content can't be used for a partnership ad.",
  other: "Meta rejected this target.",
}

/** Why the dry run failed, with Meta's words when the API attached them. Null when it passed or was not attempted. */
export function describeValidation(
  validation: PartnershipLaunchValidationDto | null,
): string | null {
  if (!validation || validation.ok) return null
  // A request without its required tags skips Meta's dry run; the API puts
  // its own "Required tags missing: {labels}" sentence in this slot. That is
  // Marathon's verdict, never Meta's, so it must not read as "Meta: …".
  if (requiredTagLabelsOf({ validation })) {
    return describeBlocker("required_tags_missing", { validation })
  }
  const head = validation.kind ? LAUNCH_VALIDATION_COPY[validation.kind] : null
  if (head && validation.message) return `${head} (Meta: ${validation.message})`
  return head ?? validation.message ?? LAUNCH_VALIDATION_COPY.other
}

/** Per-target failure classes of the create job. */
export const LAUNCH_ERROR_COPY: Record<PartnershipLaunchErrorKind, string> = {
  not_found: "The tracker row no longer exists",
  not_ad_ready: "Meta reports this post is not ad-ready",
  platform_unsupported: "Only Instagram posts can be launched",
  no_content_id: "The row has no Instagram media id",
  creator_revoked: "The creator revoked ads permission",
  brand_identity_missing:
    "The brand's Page or Instagram account could not be resolved",
  write_access_missing:
    "The brand credential has no write access to the ad account — reconnect the Instagram Insights connector",
  link_required: "The ad set needs a website link",
  video_needs_ad_code:
    "This Reel needs a creator ad code or one boost from Ads Manager",
  content_rejected: "Meta says this content can't be used for a partnership ad",
  ad_account_not_bound: "No Meta ad account is bound to this company",
  meta_error: "Meta returned an error",
}

/**
 * Kinds whose job message is already the full sentence: the fix, then Meta's
 * own words and codes, or the job's finding that the stored ad code could not
 * be read. The other kinds' messages only restate their fixed text.
 */
const LAUNCH_ERROR_KINDS_WITH_SENTENCE = new Set<PartnershipLaunchErrorKind>([
  "link_required",
  "content_rejected",
  "video_needs_ad_code",
])

/**
 * One failed target, in words: the job's message alone for the kinds where it
 * is the full sentence, the typed reason plus Meta's message for meta_error,
 * otherwise the typed reason.
 */
export function describeLaunchFailure(
  result: Pick<PartnershipLaunchTargetResultDto, "errorKind" | "message">,
): string {
  const head = result.errorKind ? LAUNCH_ERROR_COPY[result.errorKind] : null
  if (result.errorKind && result.message) {
    if (LAUNCH_ERROR_KINDS_WITH_SENTENCE.has(result.errorKind)) {
      return result.message
    }
    if (result.errorKind === "meta_error") return `${head}: ${result.message}`
  }
  return head ?? result.message ?? LAUNCH_ERROR_COPY.meta_error
}

/** "Created 3 paused ads" / "Created 2 paused ads — 1 failed". */
export function createdAdsToast(created: number, failed: number): string {
  const head = `Created ${created} paused ad${created === 1 ? "" : "s"}`
  return failed > 0 ? `${head} — ${failed} failed` : head
}
